import {
  describePlanned,
  distanceUnit,
  metresPer,
  PlanEntries,
  planProgress,
  planText,
  roundWeight,
  weightStep,
  type Exercise,
  type ExerciseType,
  type MuscleGroup,
  type PlanEntry,
  type PlannedExercise,
  type SetRecord,
  type Units,
} from '@squats/shared';
import { useState } from 'react';
import { errorText } from '../api.ts';
import { ExercisePicker } from './ExercisePicker.tsx';

/** The plan line on a workout's exercise card: what's planned, why, and how many planned sets are done. */
export function PlanLine({ planned, type, sets, units }: { planned: PlannedExercise; type: ExerciseType; sets: SetRecord[]; units: Units }) {
  const { done, hit } = planProgress(planned, type, sets, units);
  const why = describePlanned(planned, units);
  const next = hit && planned.increment && planned.weight !== null ? roundWeight(planned.weight + planned.increment, units) : null;
  return (
    <div className={`target-line${hit ? ' hit' : ''}`}>
      <p>
        <strong>Plan {planText(planned, type, units)}</strong>
        <span className="muted small">
          {' '}
          · {done} of {planned.sets} done
        </span>
      </p>
      {next !== null ? (
        <p className="muted small">
          Plan hit. Next time: {next} {units}.
        </p>
      ) : hit ? (
        <p className="muted small">Plan done.</p>
      ) : (
        why && <p className="muted small">{why}</p>
      )}
    </div>
  );
}

/** A day's planned exercises, one per line ("Bench Press  3 × 8 @ 95 lb"). */
export function PlannedList({ planned, exercises, units }: { planned: PlannedExercise[]; exercises: Map<string, Exercise>; units: Units }) {
  if (!planned.length) return null;
  return (
    <ul className="planned-list">
      {planned.map((p, i) => {
        const e = exercises.get(p.exerciseId);
        return (
          <li key={`${p.exerciseId}-${i}`}>
            <span className="grow">{e?.name ?? 'Exercise'}</span>
            <span className="muted">{planText(p, e?.type ?? 'weight_reps', units)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** A day's planned exercises as an editable list (to give that day its own plan), in the person's units. */
export const plannedToEntries = (planned: PlannedExercise[], units: Units): PlanEntry[] =>
  planned.map((p) => ({ exerciseId: p.exerciseId, sets: p.sets, reps: p.reps, durationS: p.durationS, distanceM: p.distanceM, weight: p.weight, unit: units, increment: null }));

/** Sensible numbers for an exercise just added to a plan. */
function freshEntry(exercise: Exercise, mode: 'template' | 'day', units: Units): PlanEntry {
  const base = { exerciseId: exercise.id, durationS: null, distanceM: null, weight: null, unit: units, increment: null };
  switch (exercise.type) {
    case 'weight_reps':
      return { ...base, sets: 3, reps: 8, increment: mode === 'template' ? weightStep(units) : null };
    case 'bodyweight_reps':
      return { ...base, sets: 3, reps: 10 };
    case 'hold':
      return { ...base, sets: 3, reps: null, durationS: 30 };
    case 'cardio':
      return { ...base, sets: 1, reps: null, durationS: 20 * 60 };
  }
}

/** Editable numbers for one entry, as text while typing. */
type Draft = { exerciseId: string; sets: string; reps: string; duration: string; distance: string; weight: string; increment: string; unit: Units };

const text = (n: number | null) => (n === null ? '' : String(n));
function toDraft(e: PlanEntry, type: ExerciseType): Draft {
  return {
    exerciseId: e.exerciseId,
    sets: text(e.sets),
    reps: text(e.reps),
    // Holds in seconds; cardio in minutes.
    duration: text(e.durationS === null ? null : type === 'cardio' ? +(e.durationS / 60).toFixed(2) : e.durationS),
    distance: text(e.distanceM === null ? null : +(e.distanceM / metresPer(e.unit)).toFixed(2)),
    weight: text(e.weight),
    increment: text(e.increment),
    unit: e.unit,
  };
}
function fromDraft(d: Draft, type: ExerciseType): PlanEntry {
  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  const duration = num(d.duration);
  const distance = num(d.distance);
  return {
    exerciseId: d.exerciseId,
    sets: num(d.sets) ?? 0,
    reps: type === 'weight_reps' || type === 'bodyweight_reps' ? num(d.reps) : null,
    durationS: type === 'hold' || type === 'cardio' ? (duration === null ? null : Math.round(type === 'cardio' ? duration * 60 : duration)) : null,
    distanceM: type === 'cardio' && distance !== null ? Math.round(distance * metresPer(d.unit)) : null,
    weight: type === 'weight_reps' ? num(d.weight) : null,
    unit: d.unit,
    increment: type === 'weight_reps' ? num(d.increment) : null,
  };
}

/**
 * Edit a list of planned exercises: a split's template (with how much to add each time it's hit), or one day's own
 * list (numbers exactly as written). Numbers are in each entry's own units.
 */
export function PlanEditor({
  mode,
  initial,
  exercises,
  groups,
  units,
  onSave,
  onCancel,
  extra,
}: {
  mode: 'template' | 'day';
  initial: PlanEntry[];
  exercises: Map<string, Exercise>;
  /** Muscle groups to suggest exercises for. */
  groups: readonly MuscleGroup[];
  /** Units for newly added exercises. */
  units: Units;
  onSave: (entries: PlanEntry[]) => Promise<void>;
  onCancel: () => void;
  /** Extra buttons (e.g. "Back to the template"). */
  extra?: React.ReactNode;
}) {
  const typeOf = (id: string) => exercises.get(id)?.type ?? 'weight_reps';
  const [drafts, setDrafts] = useState(() => initial.map((e) => toDraft(e, typeOf(e.exerciseId))));
  const [picking, setPicking] = useState(initial.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const update = (i: number, change: Partial<Draft>) => setDrafts((d) => d.map((x, j) => (j === i ? { ...x, ...change } : x)));
  const move = (i: number, by: -1 | 1) =>
    setDrafts((d) => {
      const next = [...d];
      [next[i], next[i + by]] = [next[i + by]!, next[i]!];
      return next;
    });

  const save = async () => {
    const entries = drafts.map((d) => fromDraft(d, typeOf(d.exerciseId)));
    const parsed = PlanEntries.safeParse(entries);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const row = typeof issue?.path[0] === 'number' ? exercises.get(drafts[issue.path[0]]!.exerciseId)?.name : undefined;
      setError(`${row ? `${row}: ` : ''}${issue?.message ?? 'Check the numbers.'}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(parsed.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (picking) {
    return (
      <div className="stack">
        <ExercisePicker
          exercises={[...exercises.values()]}
          groups={groups}
          history={[]}
          today=""
          exclude={drafts.map((d) => d.exerciseId)}
          suggestionsTitle="Suggested for these muscles"
          onPick={(id) => {
            const e = exercises.get(id);
            if (e) setDrafts((d) => [...d, toDraft(freshEntry(e, mode, units), e.type)]);
            setPicking(false);
          }}
        />
        <button type="button" className="btn" onClick={() => (drafts.length ? setPicking(false) : onCancel())}>
          {drafts.length ? 'Back to the list' : 'Cancel'}
        </button>
      </div>
    );
  }

  const field = (label: string, value: string, set: (v: string) => void) => (
    <label className="plan-field">
      <span className="muted small">{label}</span>
      <input inputMode="decimal" value={value} onChange={(e) => set(e.target.value.replace(',', '.'))} onFocus={(e) => e.target.select()} aria-label={label} />
    </label>
  );

  return (
    <div className="stack">
      {mode === 'template' ? (
        <p className="muted small">
          Every day of this split plans these. Weights start where you put them and go up by “Add” each time every planned rep is hit (10% lighter after two misses in a row). Leave
          “Add” empty to keep a weight fixed.
        </p>
      ) : (
        <p className="muted small">This day only: these numbers are used exactly as written, and the split’s template stays as it is.</p>
      )}
      {drafts.length === 0 && <p className="muted">No exercises yet.</p>}
      <ol className="plan-editor">
        {drafts.map((d, i) => {
          const type = typeOf(d.exerciseId);
          return (
            <li key={`${d.exerciseId}-${i}`} className="plan-row-edit">
              <div className="row">
                <span className="grow row-title">{exercises.get(d.exerciseId)?.name ?? 'Exercise'}</span>
                <button type="button" className="btn small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">
                  ↑
                </button>
                <button type="button" className="btn small" onClick={() => move(i, 1)} disabled={i === drafts.length - 1} aria-label="Move down">
                  ↓
                </button>
                <button
                  type="button"
                  className="btn small"
                  onClick={() => setDrafts((x) => x.filter((_, j) => j !== i))}
                  aria-label={`Remove ${exercises.get(d.exerciseId)?.name ?? 'exercise'}`}
                >
                  ✕
                </button>
              </div>
              <div className="plan-fields">
                {field('Sets', d.sets, (v) => update(i, { sets: v }))}
                {(type === 'weight_reps' || type === 'bodyweight_reps') && field('Reps', d.reps, (v) => update(i, { reps: v }))}
                {type === 'hold' && field('Seconds', d.duration, (v) => update(i, { duration: v }))}
                {type === 'cardio' && field('Minutes', d.duration, (v) => update(i, { duration: v }))}
                {type === 'cardio' && field(distanceUnit(d.unit), d.distance, (v) => update(i, { distance: v }))}
                {type === 'weight_reps' && field(`Weight (${d.unit})`, d.weight, (v) => update(i, { weight: v }))}
                {type === 'weight_reps' && mode === 'template' && field(`Add (${d.unit})`, d.increment, (v) => update(i, { increment: v }))}
              </div>
            </li>
          );
        })}
      </ol>
      <button type="button" className="btn" onClick={() => setPicking(true)}>
        + Add exercise
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row wrap">
        {extra}
        <span className="grow" />
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
          Save
        </button>
      </div>
    </div>
  );
}
